import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, AlertTriangle, X, Clock, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { deleteMyAccount, cancelMyDeletion } from '../utils/accountSettingsApi';

const font = "'Inria Serif', serif";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (_) {
    return iso;
  }
};

/**
 * Reusable Delete-account section + confirmation modal with a 14-day soft-delete
 * grace period. Two states:
 *   1. Account healthy → red "Delete my account" CTA + schedule modal.
 *   2. Account deletion pending → amber banner + "Cancel deletion" CTA.
 */
export const DeleteAccountSection = ({ dataTestIdPrefix = 'account' }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch (_) {
      return {};
    }
  });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  // Refresh user from /auth/me on mount so deletion_pending is fresh.
  useEffect(() => {
    const refresh = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data) {
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
        }
      } catch (_) {}
    };
    refresh();
  }, []);

  const isPending = !!user?.deletion_pending;
  const finalizeAt = user?.deletion_finalize_at;

  const reset = () => {
    setPassword('');
    setConfirmation('');
    setReason('');
    setLoading(false);
  };

  const closeDelete = () => {
    if (loading) return;
    setDeleteOpen(false);
    reset();
  };

  const closeCancel = () => {
    if (loading) return;
    setCancelOpen(false);
    reset();
  };

  const handleSchedule = async () => {
    if (confirmation.trim().toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE to confirm.');
      return;
    }
    if (!password) {
      toast.error('Enter your password to confirm.');
      return;
    }
    setLoading(true);
    try {
      const res = await deleteMyAccount({ password, confirmation, reason });
      // Refresh local user
      const updated = { ...user, deletion_pending: true, deletion_finalize_at: res.deletion_finalize_at };
      setUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
      toast.success(`Deletion scheduled for ${formatDate(res.deletion_finalize_at)}`);
      navigate('/account-deleted', { replace: true, state: { finalizeAt: res.deletion_finalize_at } });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not schedule deletion. Please try again.';
      toast.error(detail);
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!password) {
      toast.error('Enter your password to confirm.');
      return;
    }
    setLoading(true);
    try {
      await cancelMyDeletion({ password });
      const updated = { ...user, deletion_pending: false, deletion_finalize_at: null };
      setUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
      toast.success('Account deletion cancelled. Welcome back.');
      closeCancel();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not cancel deletion.';
      toast.error(detail);
      setLoading(false);
    }
  };

  // ── State A: deletion is pending ─────────────────────────────────────────
  if (isPending) {
    return (
      <>
        <div
          data-testid={`${dataTestIdPrefix}-deletion-pending-section`}
          style={{
            marginTop: 24,
            padding: 16,
            borderRadius: 14,
            border: '1px solid var(--card-border)',
            background: 'var(--status-warning-surface)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Clock size={20} color="var(--status-warning-text)" />
            <p style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--status-warning-text)', margin: 0 }}>
              Deletion scheduled
            </p>
          </div>
          <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: '0 0 12px', lineHeight: 1.5 }}>
            Your account is set to be permanently deleted on{' '}
            <strong>{finalizeAt ? formatDate(finalizeAt) : 'the scheduled date'}</strong>.
            You can change your mind any time before that date.
          </p>
          <button
            type="button"
            onClick={() => setCancelOpen(true)}
            data-testid={`${dataTestIdPrefix}-cancel-deletion-button`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 16px',
              border: 'none',
              background: 'var(--status-warning-text)',
              color: '#fff',
              fontFamily: font,
              fontSize: 14,
              fontWeight: 700,
              borderRadius: 10,
              cursor: 'pointer',
            }}
          >
            <Undo2 size={16} /> Cancel deletion
          </button>
        </div>

        {cancelOpen && (
          <div
            data-testid={`${dataTestIdPrefix}-cancel-deletion-modal`}
            onClick={closeCancel}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                maxWidth: 420,
                background: 'var(--surface)',
                borderRadius: 16,
                padding: 22,
                boxShadow: 'var(--premium-shadow-main)',
                fontFamily: font,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>
                  Cancel deletion?
                </h3>
                <button
                  type="button"
                  onClick={closeCancel}
                  disabled={loading}
                  data-testid={`${dataTestIdPrefix}-cancel-modal-close`}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text)' }}
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>

              <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.55, margin: '0 0 16px' }}>
                This will keep your account active and remove the scheduled deletion.
              </p>

              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
                Confirm with your password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={`${dataTestIdPrefix}-cancel-modal-password`}
                placeholder="Enter your password"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--hairline)',
                  borderRadius: 10,
                  fontSize: 14,
                  fontFamily: font,
                  marginBottom: 18,
                  boxSizing: 'border-box',
                  background: 'var(--surface)',
                  color: 'var(--text)',
                }}
              />

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={closeCancel}
                  disabled={loading}
                  data-testid={`${dataTestIdPrefix}-cancel-modal-cancel`}
                  style={{
                    flex: 1,
                    padding: '11px 0',
                    border: '1px solid var(--hairline)',
                    background: 'var(--surface)',
                    color: 'var(--text)',
                    fontFamily: font,
                    fontSize: 14,
                    fontWeight: 600,
                    borderRadius: 10,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  Keep deletion
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={loading}
                  data-testid={`${dataTestIdPrefix}-cancel-modal-submit`}
                  style={{
                    flex: 1,
                    padding: '11px 0',
                    border: 'none',
                    background: 'var(--status-success-text)',
                    color: '#fff',
                    fontFamily: font,
                    fontSize: 14,
                    fontWeight: 700,
                    borderRadius: 10,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  {loading ? 'Cancelling…' : 'Cancel deletion'}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ── State B: account is healthy ──────────────────────────────────────────
  return (
    <>
      <div
        data-testid={`${dataTestIdPrefix}-delete-account-section`}
        style={{
          marginTop: 24,
          padding: 16,
          borderRadius: 14,
          border: '1px solid var(--card-border)',
          background: 'var(--status-danger-surface)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Trash2 size={20} color="var(--status-danger-text)" />
          <p style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--status-danger-text)', margin: 0 }}>
            Delete my account
          </p>
        </div>
        <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: '0 0 12px', lineHeight: 1.5 }}>
          Schedule your account for deletion. You will have <strong>14 days</strong> to cancel
          before personal data is permanently removed. Financial records required by law are kept
          for up to 10 years in anonymised form.{' '}
          <a href="/delete-account" target="_blank" rel="noreferrer" style={{ color: 'var(--status-danger-text)', fontWeight: 600 }}>
            Learn more
          </a>
        </p>
        <button
          type="button"
          onClick={() => setDeleteOpen(true)}
          data-testid={`${dataTestIdPrefix}-delete-account-button`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 16px',
            border: '1px solid var(--status-danger-text)',
            background: 'var(--surface)',
            color: 'var(--status-danger-text)',
            fontFamily: font,
            fontSize: 14,
            fontWeight: 700,
            borderRadius: 10,
            cursor: 'pointer',
          }}
        >
          <Trash2 size={16} /> Delete my account
        </button>
      </div>

      {deleteOpen && (
        <div
          data-testid={`${dataTestIdPrefix}-delete-account-modal`}
          onClick={closeDelete}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 420,
              background: 'var(--surface)',
              borderRadius: 16,
              padding: 22,
              boxShadow: 'var(--premium-shadow-main)',
              fontFamily: font,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={22} color="var(--status-danger-text)" />
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>
                  Delete account?
                </h3>
              </div>
              <button
                type="button"
                onClick={closeDelete}
                disabled={loading}
                data-testid={`${dataTestIdPrefix}-delete-modal-close`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text)' }}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.55, margin: '0 0 16px' }}>
              Your account will enter a <strong>14-day grace period</strong>. You can sign in any
              time during that window and tap <em>Cancel deletion</em> to restore it. After 14 days
              your name, email, photos and chat history are permanently anonymised.
            </p>

            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
              Current password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid={`${dataTestIdPrefix}-delete-modal-password`}
              placeholder="Enter your password"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--hairline)',
                borderRadius: 10,
                fontSize: 14,
                fontFamily: font,
                marginBottom: 14,
                boxSizing: 'border-box',
                background: 'var(--surface)',
                color: 'var(--text)',
              }}
            />

            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
              Type <span style={{ color: 'var(--status-danger-text)' }}>DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              data-testid={`${dataTestIdPrefix}-delete-modal-confirm`}
              placeholder="DELETE"
              autoCapitalize="characters"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--hairline)',
                borderRadius: 10,
                fontSize: 14,
                fontFamily: font,
                marginBottom: 14,
                boxSizing: 'border-box',
                letterSpacing: 1,
                background: 'var(--surface)',
                color: 'var(--text)',
              }}
            />

            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
              Reason (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid={`${dataTestIdPrefix}-delete-modal-reason`}
              placeholder="Help us improve Allure..."
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--hairline)',
                borderRadius: 10,
                fontSize: 14,
                fontFamily: font,
                marginBottom: 18,
                boxSizing: 'border-box',
                resize: 'vertical',
                background: 'var(--surface)',
                color: 'var(--text)',
              }}
            />

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={closeDelete}
                disabled={loading}
                data-testid={`${dataTestIdPrefix}-delete-modal-cancel`}
                style={{
                  flex: 1,
                  padding: '11px 0',
                  border: '1px solid var(--hairline)',
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  fontFamily: font,
                  fontSize: 14,
                  fontWeight: 600,
                  borderRadius: 10,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSchedule}
                disabled={loading}
                data-testid={`${dataTestIdPrefix}-delete-modal-submit`}
                style={{
                  flex: 1,
                  padding: '11px 0',
                  border: 'none',
                  background: 'var(--status-danger-text)',
                  color: '#fff',
                  fontFamily: font,
                  fontSize: 14,
                  fontWeight: 700,
                  borderRadius: 10,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? 'Scheduling…' : 'Schedule deletion'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DeleteAccountSection;
